import { defineStore, acceptHMRUpdate } from 'pinia';
import { formatDate } from '@/utils/date';
import { exportApi } from '@/services/exportService';
import { useArStatsStore } from './stats/useARStats';
import { useDenialsStore } from './stats/useDenialsStats';
import { useWorkGapStore } from './stats/useWorkGapStats';
import { useActivityStatsStore } from './stats/useActivityStats';
import { useInflowOutflowStore } from './gameplan/useInflowOutflowStore';
import { useProductivityStore } from './useProductivityStore';

/** Keep rows created locally while a fetch was in flight; server list wins on id collisions. */
export function mergeExportLists(fetched, existing) {
  const serverList = fetched ?? [];
  const local = existing ?? [];
  const serverIds = new Set(serverList.map(e => e.export_id));
  const onlyLocal = local.filter(e => !serverIds.has(e.export_id));
  return [...onlyLocal, ...serverList];
}

/** One in-flight GET per (serviceType, exportType) so parallel callers share a single request. */
const fetchInflight = new Map();

function fetchExportsKey(serviceType, exportType) {
  return exportType ? `${serviceType}:${exportType}` : `flat:${serviceType}`;
}

export const useExports = defineStore('exports', {
  state: () => ({
    exports: {}, // Store exports by service type
    loading: false,
  }),
  getters: {
    // Get all exports for the stats dashboard
    getExports: state => serviceType => state.exports[serviceType] || [],
    getLoading: state => state.loading,
    getExportById: state => (serviceType, exportType, exportId) => {
      let serviceExports;
      if (exportType) {
        serviceExports = state.exports[serviceType][exportType] || [];
      }
      serviceExports = state.exports[serviceType] || [];
      return serviceExports.find(exp => exp.export_id === exportId);
    },
    getExportsByStatus: state => (serviceType, exportType, status) => {
      let serviceExports;
      if (exportType) {
        serviceExports = state.exports[serviceType][exportType] || [];
      }
      serviceExports = state.exports[serviceType] || [];
      return serviceExports.filter(exp => exp.status === status);
    },
    // Get all exports for the ar dashboard, eg: stats.ar
    getExportsByType: state => (serviceType, exportType) => {
      const serviceExports = state.exports[serviceType] || [];
      return serviceExports[exportType] || [];
    },
    // Can be extended to support future service types
    _getFiltersApplied: () => (serviceType, exportType) => {
      let filters = {};
      let pagination = {};
      let store;
      if (serviceType == 'stats') {
        // get filters from the correct store
        switch (exportType) {
          case 'ar_details':
            store = useArStatsStore();
            filters = store.build_export_payload() || {};
            pagination = store.get_pagination() || {};
            break;
          case 'denials_details':
            store = useDenialsStore();
            filters = store._buildPayload().filter || {};
            pagination = store.get_pagination() || {};
            break;
          case 'work_gap_details':
            store = useWorkGapStore();
            filters = store._buildPayload().filter || {};
            pagination = store.get_pagination() || {};
            break;
          case 'work_activity_details':
            store = useActivityStatsStore();
            filters = store._buildPayload().filter || {};
            pagination = store.get_pagination() || {};
            break;
          default:
            filters = filters || {};
            break;
        }
      }
      if (serviceType == 'inoutflow') {
        switch (exportType) {
          case 'inoutflow_details':
            store = useInflowOutflowStore();
            filters = store.getExportFilters || {};
            pagination = store.detailPagination || {};
            break;
          default:
            filters = filters || {};
            break;
        }
      }
      if (serviceType == 'productivity') {
        switch (exportType) {
          case 'productivity_details':
            store = useProductivityStore();
            filters = store.getExportFilters || {};
            pagination = {
              sortKey: store.detailsPagination?.sortKey || null,
              sortOrder: store.detailsPagination?.sortOrder || null,
            };
            break;
          default:
            filters = filters || {};
            break;
        }
      }
      return { filters, pagination };
    },
  },
  actions: {
    /**
     * Get exports for a specific service type and export type
     * @param {*} serviceType eg: 'stats', 'gameplan'
     * @param {*} exportType eg: 'ar', 'denials'
     */
    async fetchExports(serviceType, exportType = '') {
      if (
        exportType &&
        Array.isArray(this.exports[serviceType]?.[exportType])
      ) {
        return true;
      }

      const key = fetchExportsKey(serviceType, exportType);
      const existing = fetchInflight.get(key);
      if (existing) {
        return existing;
      }

      const pending = (async () => {
        this.loading = true;
        this.error = null;
        try {
          const data = await exportApi.getExports(exportType);
          if (exportType) {
            this.exports[serviceType] = this.exports[serviceType] || {};
            const prev = this.exports[serviceType][exportType];
            this.exports[serviceType][exportType] = mergeExportLists(
              data.exports,
              Array.isArray(prev) ? prev : []
            );
          } else {
            const prev = this.exports[serviceType];
            this.exports[serviceType] = mergeExportLists(
              data.exports,
              Array.isArray(prev) ? prev : []
            );
          }
          return true;
        } finally {
          this.loading = false;
          fetchInflight.delete(key);
        }
      })();

      fetchInflight.set(key, pending);
      return pending;
    },

    /**
     * Create a new export
     * @param {*} serviceType eg: 'stats', 'gameplan'
     * @param {*} exportType eg: 'ar', 'denials'
     */
    async createExport(serviceType, exportType = '') {
      this.loading = true;
      this.error = null;
      try {
        const { filters, pagination } = this._getFiltersApplied(
          serviceType,
          exportType
        );
        const alias = `${exportType}_${formatDate(new Date(), 'yyyyMMddHHmmss', false)}`;
        const data = await exportApi.createExport({
          serviceType,
          exportType,
          filters,
          pagination,
          alias,
        });

        const newExport = {
          export_id: data.export_id,
          export_alias: alias,
          export_type: exportType,
          status: 'pending',
          created_at: new Date().toISOString(),
          expiration_timest: data.expiration,
        };

        if (exportType) {
          this.exports[serviceType] = this.exports[serviceType] || {};
          this.exports[serviceType][exportType] =
            this.exports[serviceType][exportType] || [];
          this.exports[serviceType][exportType].unshift(newExport);
        } else {
          this.exports[serviceType] = this.exports[serviceType] || [];
          this.exports[serviceType].unshift(newExport);
        }

        return data;
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    async updateExport(exportId, updates) {
      this.error = null;
      try {
        return await exportApi.updateExport(exportId, updates);
      } catch (error) {
        this.error = error;
      }
    },
    updateUrl(exportId, presignedUrl, serviceType, exportType) {
      const exportItem = this.exports[serviceType]?.[exportType]?.find(
        item => item.export_id === exportId
      );
      if (exportItem) {
        exportItem.presigned_url = presignedUrl;
      }
      return exportItem;
    },
    setExportReady(
      serviceType,
      exportId,
      presignedUrl,
      expirationTimest,
      exportType = ''
    ) {
      const UtcExpirationTimest = new Date(
        expirationTimest + 'Z'
      ).toUTCString();
      // find export, update it to ready, eg: stats.ar_details,
      if (exportType) {
        const exportItem = this.exports[serviceType]?.[exportType]?.find(
          item => item.export_id === exportId
        );
        if (exportItem) {
          exportItem.status = 'completed';
          exportItem.presigned_url = presignedUrl;
          exportItem.expiration_timest = UtcExpirationTimest;
        }
      }
      // gameplan
      else {
        const exportItem = this.exports[serviceType]?.find(
          item => item.export_id === exportId
        );
        if (exportItem) {
          exportItem.status = 'completed';
          exportItem.presigned_url = presignedUrl;
          exportItem.expiration_timest = UtcExpirationTimest;
        }
      }
      return;
    },
    setExportStatus(serviceType, exportId, status, exportType = '') {
      // find export, update it to ready, eg: stats.ar_details,
      if (exportType) {
        const exportItem = this.exports[serviceType]?.[exportType]?.find(
          item => item.export_id === exportId
        );
        if (exportItem) {
          exportItem.status = status;
        }
      }
      // gameplan
      else {
        const exportItem = this.exports[serviceType]?.find(
          item => item.export_id === exportId
        );
        if (exportItem) {
          exportItem.status = status;
        }
      }
      return;
    },
  },
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useExports, import.meta.hot));
}
